import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from "recharts";
import { TrendingUp, TrendingDown, Clock, Calendar, Target, Layers } from "lucide-react";
import { PageHeader, Loading } from "@/components/Common";
import { EUR, EURc, pnlColor } from "@/lib/format";
import api from "@/lib/api";

function DnaCard({ icon: Icon, label, best, worst, fmt = (x) => x?.key }) {
  return (
    <div className="dlx-card p-5">
      <div className="mb-4 flex items-center gap-2">
        <Icon size={15} className="text-gold" />
        <span className="font-sans text-[11px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between rounded-lg border border-profit/20 bg-profit/5 px-3 py-2">
          <div>
            <div className="font-sans text-[9px] uppercase tracking-widest text-profit/70">Migliore</div>
            <div className="font-sans text-sm text-white">{best ? fmt(best) : "—"}</div>
          </div>
          {best && <span className="font-mono text-sm font-semibold text-profit">{EURc(best.net_pnl)}</span>}
        </div>
        <div className="flex items-center justify-between rounded-lg border border-loss/20 bg-loss/5 px-3 py-2">
          <div>
            <div className="font-sans text-[9px] uppercase tracking-widest text-loss/70">Peggiore</div>
            <div className="font-sans text-sm text-white">{worst ? fmt(worst) : "—"}</div>
          </div>
          {worst && <span className="font-mono text-sm font-semibold text-loss">{EURc(worst.net_pnl)}</span>}
        </div>
      </div>
    </div>
  );
}

export default function TradingDNA() {
  const { data, isLoading } = useQuery({ queryKey: ["dna"], queryFn: () => api.get("/dna").then((r) => r.data) });
  if (isLoading || !data?.summary) return <Loading />;
  const s = data.summary;

  const radar = (data.sessions || []).map((x) => ({ subject: x.key.replace("/NY", ""), wr: x.win_rate }));
  const setupBars = (data.setups || []).map((x) => ({ name: x.key, pnl: x.net_pnl }));

  const summaryTiles = [
    { l: "Win Rate", v: `${s.win_rate}%` },
    { l: "Avg Winner", v: EUR(s.avg_win), tone: "profit" },
    { l: "Avg Loser", v: EUR(s.avg_loss), tone: "loss" },
    { l: "Average R", v: s.avg_r.toFixed(2), tone: s.avg_r >= 0 ? "profit" : "loss" },
    { l: "Expectancy", v: EUR(s.expectancy) },
    { l: "Avg Duration", v: `${s.avg_duration_min}m` },
  ];

  return (
    <div data-testid="dna-page">
      <PageHeader title="Trading DNA" subtitle="Che tipo di trader sei secondo i tuoi dati" />

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="dlx-card dlx-grain mb-6 p-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {summaryTiles.map((t) => (
            <div key={t.l} className="text-center">
              <div className="font-sans text-[10px] uppercase tracking-widest text-zinc-500">{t.l}</div>
              <div className={`mt-2 font-mono text-xl font-bold ${t.tone === "profit" ? "text-profit" : t.tone === "loss" ? "text-loss" : "text-white"}`}>{t.v}</div>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <div className="dlx-card p-5 sm:p-6">
          <div className="mb-3 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Win Rate per Sessione</div>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={radar}>
              <PolarGrid stroke="rgba(255,255,255,0.08)" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: "#A1A1AA", fontSize: 11 }} />
              <Radar dataKey="wr" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.25} strokeWidth={2} />
              <Tooltip contentStyle={{ background: "#0F0F12", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v}%`, "Win Rate"]} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="dlx-card p-5 sm:p-6">
          <div className="mb-3 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">P&L per Setup</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={setupBars} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: "#52525B", fontSize: 9 }} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={{ background: "#0F0F12", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, fontSize: 12 }} formatter={(v) => [EUR(v), "P&L"]} />
              <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                {setupBars.map((e, i) => <Cell key={i} fill={e.pnl >= 0 ? "#10B981" : "#EF4444"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <DnaCard icon={Clock} label="Sessione" best={data.best_session} worst={data.worst_session} />
        <DnaCard icon={Clock} label="Ora del Giorno" best={data.best_hour} worst={data.worst_hour} fmt={(x) => `${x.key}:00`} />
        <DnaCard icon={Calendar} label="Giorno" best={data.best_day} worst={data.worst_day} />
        <DnaCard icon={TrendingUp} label="Direzione" best={data.best_direction} worst={data.worst_direction} />
        <DnaCard icon={Layers} label="Setup" best={data.best_setup} worst={data.worst_setup} />
      </div>
    </div>
  );
}
